import { z } from 'zod';
import type {
  CategoryOption,
  ChargeBreakdown,
  ChecklistItem,
  CompanyLookupItem,
  LaunchedMaintenance,
  PaymentMethodOption,
  ServiceOrder,
  ServiceOrderPart,
  ServiceOrderSignature,
} from '@/domain/service';

/**
 * Schemas Zod da fronteira C4 do Corte 2 — tolerantes na borda (WhenWritingNull), estritos no
 * domínio (C3). Formatos reais da tech-api 1.35.0 (TechServiceOrderDtos, TechLaunchedRequestDtos,
 * TechMaintenanceDtos).
 */

const ChecklistItemSchema = z
  .object({
    id: z.string().min(1),
    description: z.string().min(1),
    isDone: z.boolean(),
    displayOrder: z.number(),
  })
  .transform((raw): ChecklistItem => ({
    id: raw.id,
    description: raw.description,
    isDone: raw.isDone,
    displayOrder: raw.displayOrder,
  }));

const ServiceOrderPartSchema = z
  .object({
    id: z.string().min(1),
    description: z.string(),
    quantity: z.number(),
    unitAmount: z.number(),
    total: z.number(),
    isNoCharge: z.boolean().optional(),
    noChargeReason: z.string().nullish(),
  })
  .transform((raw): ServiceOrderPart => ({
    id: raw.id,
    description: raw.description,
    quantity: raw.quantity,
    unitAmount: raw.unitAmount,
    total: raw.total,
    isNoCharge: raw.isNoCharge ?? false,
    noChargeReason: raw.noChargeReason ?? null,
  }));

const ServiceOrderSignatureSchema = z
  .object({
    id: z.string().min(1),
    signerName: z.string(),
    signedAt: z.string(),
    signerLatitude: z.number().nullish(),
    signerLongitude: z.number().nullish(),
    signerLocationAccuracyM: z.number().nullish(),
    signerLocationAbsenceReason: z.string().nullish(),
  })
  .transform((raw): ServiceOrderSignature => ({
    id: raw.id,
    signerName: raw.signerName,
    signedAt: raw.signedAt,
    signerLatitude: raw.signerLatitude ?? null,
    signerLongitude: raw.signerLongitude ?? null,
    signerLocationAccuracyM: raw.signerLocationAccuracyM ?? null,
    signerLocationAbsenceReason: raw.signerLocationAbsenceReason ?? null,
  }));

const ChargeBreakdownSchema = z
  .object({
    serviceAmountDue: z.number(),
    urgencySurchargeAmount: z.number(),
    urgencyAmountDue: z.number(),
    partsTotal: z.number(),
    partsAmountDue: z.number(),
    clinicAmountDue: z.number(),
    platformFundedAmount: z.number(),
    coverageCode: z.string(),
  })
  .transform((raw): ChargeBreakdown => ({
    serviceAmountDue: raw.serviceAmountDue,
    urgencySurchargeAmount: raw.urgencySurchargeAmount,
    urgencyAmountDue: raw.urgencyAmountDue,
    partsTotal: raw.partsTotal,
    partsAmountDue: raw.partsAmountDue,
    clinicAmountDue: raw.clinicAmountDue,
    platformFundedAmount: raw.platformFundedAmount,
    coverageCode: raw.coverageCode,
  }));

const ServiceOrderSchema = z
  .object({
    id: z.string().min(1),
    maintenanceRequestId: z.string().min(1),
    diagnosis: z.string().nullish(),
    servicePerformed: z.string().nullish(),
    timeSpentMinutes: z.number().nullish(),
    // B14 — o TechServiceOrderResponse já devolve os dois campos, mas eles ficavam fora do parse
    // e a tela reabria vazia. Ausentes no payload (não estão em `required`), viram null.
    recommendations: z.string().nullish(),
    nextMaintenance: z.string().nullish(),
    parts: z.array(ServiceOrderPartSchema).optional(),
    partsTotal: z.number().optional(),
    photosBefore: z.number().optional(),
    photosAfter: z.number().optional(),
    signature: ServiceOrderSignatureSchema.nullish(),
    missingFields: z.array(z.string()).optional(),
    isClosed: z.boolean(),
    closedAt: z.string().nullish(),
    chargeBreakdown: ChargeBreakdownSchema.nullish(),
  })
  .transform((raw): ServiceOrder => ({
    id: raw.id,
    maintenanceRequestId: raw.maintenanceRequestId,
    diagnosis: raw.diagnosis ?? null,
    servicePerformed: raw.servicePerformed ?? null,
    timeSpentMinutes: raw.timeSpentMinutes ?? null,
    recommendations: raw.recommendations ?? null,
    nextMaintenance: raw.nextMaintenance ?? null,
    parts: raw.parts ?? [],
    partsTotal: raw.partsTotal ?? 0,
    photosBefore: raw.photosBefore ?? 0,
    photosAfter: raw.photosAfter ?? 0,
    signature: raw.signature ?? null,
    missingFields: raw.missingFields ?? [],
    isClosed: raw.isClosed,
    closedAt: raw.closedAt ?? null,
    chargeBreakdown: raw.chargeBreakdown ?? null,
  }));

const CompanyLookupItemSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    city: z.string().nullish(),
    state: z.string().nullish(),
  })
  .transform((raw): CompanyLookupItem => ({
    id: raw.id,
    name: raw.name,
    city: raw.city ?? null,
    state: raw.state ?? null,
  }));

const LaunchedMaintenanceSchema = z
  .object({
    id: z.string().min(1),
    requestNumber: z.string().nullish(),
    companyId: z.string().min(1),
    companyName: z.string().min(1),
    origin: z.string(),
    statusId: z.number(),
    alreadyPaid: z.boolean(),
    amountDue: z.number().optional(),
    priceTier: z.string().nullish(),
    isCoveredByPlan: z.boolean().optional(),
    paidExternallyAt: z.string().nullish(),
    expectedTransferAmount: z.number().optional(),
    cashPaymentAmount: z.number().nullish(),
    cashPaymentReceivedAt: z.string().nullish(),
    paymentReceiptId: z.string().nullish(),
    transferReceiptId: z.string().nullish(),
  })
  .transform((raw): LaunchedMaintenance => ({
    id: raw.id,
    requestNumber: raw.requestNumber ?? null,
    companyId: raw.companyId,
    companyName: raw.companyName,
    origin: raw.origin,
    statusId: raw.statusId,
    alreadyPaid: raw.alreadyPaid,
    amountDue: raw.amountDue ?? 0,
    priceTier: raw.priceTier ?? null,
    isCoveredByPlan: raw.isCoveredByPlan ?? false,
    paidExternallyAt: raw.paidExternallyAt ?? null,
    expectedTransferAmount: raw.expectedTransferAmount ?? 0,
    cashPaymentAmount: raw.cashPaymentAmount ?? null,
    cashPaymentReceivedAt: raw.cashPaymentReceivedAt ?? null,
    paymentReceiptId: raw.paymentReceiptId ?? null,
    transferReceiptId: raw.transferReceiptId ?? null,
  }));

export function parseChecklist(payload: unknown): ChecklistItem[] {
  return z.array(ChecklistItemSchema).parse(payload);
}

export function parseServiceOrder(payload: unknown): ServiceOrder {
  return ServiceOrderSchema.parse(payload);
}

export function parseCompanyLookup(payload: unknown): CompanyLookupItem[] {
  return z.array(CompanyLookupItemSchema).parse(payload);
}

export function parseLaunchedMaintenance(payload: unknown): LaunchedMaintenance {
  return LaunchedMaintenanceSchema.parse(payload);
}

// ── Corte 2.1 — opções do fluxo de atendimento ────────────────────────────────────────────

/** MaintenancePaymentMethodResponse — o fluxo usa só id + nome; Details/IsActive ficam fora. */
const PaymentMethodOptionSchema = z
  .object({
    id: z.number(),
    methodName: z.string().min(1),
  })
  .transform((raw): PaymentMethodOption => ({
    id: raw.id,
    methodName: raw.methodName,
  }));

/** EquipmentCategoryResponse — lookup de categorias (id/code/name; demais campos fora do fluxo). */
const CategoryOptionSchema = z
  .object({
    id: z.number(),
    code: z.string().min(1),
    name: z.string().min(1),
  })
  .transform((raw): CategoryOption => ({
    id: raw.id,
    code: raw.code,
    name: raw.name,
  }));

/** CategoryStepSuggestionsResponse — o fluxo consome só as sugestões (resto é eco do request). */
const StepSuggestionsSchema = z.object({
  suggestions: z.array(z.string()).default([]),
});

export function parsePaymentMethods(payload: unknown): PaymentMethodOption[] {
  return z.array(PaymentMethodOptionSchema).parse(payload);
}

export function parseStepSuggestions(payload: unknown): { suggestions: string[] } {
  return StepSuggestionsSchema.parse(payload);
}

export function parseEquipmentCategories(payload: unknown): CategoryOption[] {
  return z.array(CategoryOptionSchema).parse(payload);
}
